import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { cn } from "@/lib/utils"
import { Cross2Icon } from "@radix-ui/react-icons"
import { RecordOpener } from "@/components/mobile/RecordOpener"
import { SheetGrabber } from "@/components/mobile/SheetGrabber"
import { isTouchViewport, useIsMobile } from "@/hooks/use-mobile"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      // Phones dim the page behind a sheet more lightly, like iOS.
      "max-md:bg-black/40",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * How the dialog presents below the md breakpoint. Desktop is unchanged.
 * - sheet: a bottom sheet with a grabber that can be swiped down (default)
 * - fullscreen: covers the screen, for editors and long forms
 * - dialog: stays a centred card, inset from the screen edges
 */
export type DialogMobileVariant = "sheet" | "fullscreen" | "dialog"

// The slide-up animation replaces the desktop zoom and slide-from-centre.
const SLIDE_UP =
  "max-md:duration-300 max-md:data-[state=open]:slide-in-from-left-0 max-md:data-[state=closed]:slide-out-to-left-0 max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom max-md:data-[state=open]:zoom-in-100 max-md:data-[state=closed]:zoom-out-100"

// max-md rules sit after the unprefixed ones in the CSS, so on phones they win
// over both the defaults and the widths callers pass (max-w-[800px], h-[80vh]…).
// Callers can still adjust the phone layout with their own max-md: classes.
// mx-auto keeps the panel centred from 640px, where callers' sm:max-w-* apply.
const mobileDialogClasses: Record<DialogMobileVariant, string> = {
  sheet: cn(
    "max-md:inset-x-0 max-md:bottom-[var(--keyboard-inset,0px)] max-md:left-0 max-md:top-auto max-md:mx-auto max-md:w-full max-md:min-w-0 max-md:max-w-none max-md:translate-x-0 max-md:translate-y-0 max-md:max-h-[calc(var(--app-height)-var(--safe-top)-var(--call-bar-height,0px)-1.5rem)] max-md:overflow-y-auto max-md:overscroll-contain max-md:rounded-b-none max-md:rounded-t-2xl max-md:border-x-0 max-md:border-b-0",
    SLIDE_UP
  ),
  // During a call the compact call bar (z-60) sits under the status bar; the
  // screen starts below it, and the caller's own safe-top padding still works.
  fullscreen: cn(
    "max-md:inset-0 max-md:left-0 max-md:top-[var(--call-bar-height,0px)] max-md:mx-auto max-md:h-[calc(var(--app-height)-var(--call-bar-height,0px))] max-md:w-full max-md:min-w-0 max-md:max-w-none max-md:max-h-none max-md:translate-x-0 max-md:translate-y-0 max-md:overflow-y-auto max-md:overscroll-contain max-md:rounded-none max-md:border-0",
    SLIDE_UP
  ),
  dialog:
    "max-md:w-[calc(100%-2rem)] max-md:min-w-0 max-md:max-h-[calc(var(--app-height)-4rem)] max-md:overflow-y-auto max-md:overscroll-contain max-md:rounded-2xl",
}

// Medium screens (landscape phones are only ~390px tall, iPads, narrow
// windows) keep the centred desktop dialog, but it fits the visible height
// and scrolls inside instead of running off the top and bottom.
const MEDIUM_DIALOG_CLASS =
  "md:max-xl:max-h-[calc(var(--app-height)-2rem)] md:max-xl:overflow-y-auto md:max-xl:overscroll-contain"

// A wide dialog on a landscape phone stays clear of the notch and rounded
// corners at the sides. The insets are 0 on tablets and desktop, where this
// is the same as w-full.
const MEDIUM_DIALOG_WIDTH =
  "md:max-xl:w-[calc(100%-var(--safe-left)-var(--safe-right))]"
// Any width the caller sets, at any breakpoint (w-[90vw], sm:w-[600px]).
const ownWidth = /(?:^|\s)(?:\S+:)?!?(?:w|size)-/

// p-0, or max-md:p-0 for a caller that keeps its desktop padding.
const zeroTop = /(?:^|\s)(?:max-md:)?p[ty]?-0(?=\s|$)/
const zeroBottom = /(?:^|\s)(?:max-md:)?p[by]?-0(?=\s|$)/
const ownTop = /(?:^|\s)-?top-/

/**
 * Phone padding: room for the grabber, and the home indicator below. A caller
 * that passes p-0 lays out its own edges, so it only gets the safe area.
 */
function mobileDialogPadding(variant: DialogMobileVariant, className = "") {
  if (variant === "dialog") return ""
  const top = zeroTop.test(className)
    ? variant === "fullscreen" && "max-md:pt-[var(--safe-top)]"
    : variant === "fullscreen"
      ? "max-md:pt-[max(1.5rem,var(--safe-top))]"
      : "max-md:pt-7"
  const bottom = zeroBottom.test(className)
    ? "max-md:pb-[var(--safe-bottom)] max-md:kb-open:pb-0"
    : "max-md:pb-[max(1.5rem,var(--safe-bottom))] max-md:kb-open:pb-6"
  return cn(top, bottom)
}

const mobileCloseClasses: Record<DialogMobileVariant, string> = {
  sheet: "max-md:right-1.5 max-md:top-1.5",
  fullscreen: "max-md:right-1.5 max-md:top-[calc(var(--safe-top)+0.375rem)]",
  dialog: "max-md:right-1.5 max-md:top-1.5",
}

// The caller styles the close button through a child selector
// (max-md:[&>button:last-child]:hidden), so it stays a direct child.
const childSelector = /\[&>|(?:^|\s)\*:/

/**
 * A phone sheet scrolls as a whole, so the grabber and the close button sit
 * in a layer as tall as the scrolled content and stick to the top of it:
 * they stay in reach however far down the sheet is scrolled, as in a native
 * sheet. The layer's height follows the content as it grows or shrinks, and
 * the bar sticks at the sheet's edge rather than inside its top padding.
 */
function usePinnedChrome(content: HTMLElement | null, layer: HTMLElement | null) {
  React.useLayoutEffect(() => {
    const bar = layer?.firstElementChild as HTMLElement | null | undefined
    if (!content || !layer || !bar) return
    const update = () => {
      bar.style.top = `-${getComputedStyle(content).paddingTop}`
      layer.style.height = "0px"
      layer.style.height = `${content.scrollHeight}px`
    }
    const resizeObserver = new ResizeObserver(update)
    const observe = () => {
      resizeObserver.disconnect()
      resizeObserver.observe(content)
      for (const child of Array.from(content.children)) {
        if (child !== layer) resizeObserver.observe(child)
      }
    }
    const mutationObserver = new MutationObserver(() => {
      observe()
      update()
    })
    observe()
    mutationObserver.observe(content, { childList: true })
    update()
    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [content, layer])
}

/** A selector (the first match on screen wins) or a function. */
type ReturnFocusTarget = string | (() => HTMLElement | null | undefined)

const ReturnFocusContext = React.createContext<ReturnFocusTarget | undefined>(undefined)

/**
 * Gives the dialog inside a returnFocusTo, for a dialog component that
 * doesn't pass one to its DialogContent itself.
 */
const DialogReturnFocus = ({ to, children }: { to: ReturnFocusTarget; children: React.ReactNode }) => (
  <ReturnFocusContext.Provider value={to}>{children}</ReturnFocusContext.Provider>
)

const isShown = (el: Element) => el.isConnected && el.getClientRects().length > 0 && !el.closest("[inert]")

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    mobileVariant?: DialogMobileVariant
    /**
     * Leave out the corner close button: true everywhere, "mobile" below md
     * only (for phone screens that draw their own Cancel / Done header).
     */
    hideClose?: boolean | "mobile"
    /**
     * For a dialog opened without a DialogTrigger: hand focus back to what
     * opened it with a mouse too, and when that has gone (a rotation or
     * resize swapped the layout underneath) to this instead, the control
     * that does the same in the new layout.
     */
    returnFocusTo?: ReturnFocusTarget
  }
>(({ className, children, mobileVariant = "sheet", hideClose = false, returnFocusTo: returnFocusToProp, onOpenAutoFocus, onCloseAutoFocus, ...props }, ref) => {
  const isMobile = useIsMobile()
  const opener = React.useRef<HTMLElement | null>(null)
  const inheritedReturnFocusTo = React.useContext(ReturnFocusContext)
  const returnFocusTo = returnFocusToProp ?? inheritedReturnFocusTo
  // A fullscreen dialog without its close button has nothing to pin.
  const pinChrome =
    isMobile &&
    mobileVariant !== "dialog" &&
    !(hideClose && mobileVariant !== "sheet") &&
    !childSelector.test(className ?? "")
  const [contentNode, setContentNode] = React.useState<HTMLDivElement | null>(null)
  const [layerNode, setLayerNode] = React.useState<HTMLDivElement | null>(null)
  usePinnedChrome(pinChrome ? contentNode : null, layerNode)
  const contentRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      setContentNode(node)
      if (typeof ref === "function") ref(node)
      else if (ref) ref.current = node
    },
    [ref]
  )

  const close = hideClose === true ? null : (
    <DialogPrimitive.Close
      className={cn(
        hideClose === "mobile" && "max-md:hidden",
        "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground max-md:rounded-full max-md:p-3 max-md:data-[state=open]:bg-transparent",
        mobileCloseClasses[mobileVariant],
        // Sheets: a filled circle, so it stays legible over content that
        // scrolls underneath it.
        mobileVariant !== "dialog" && "max-md:p-[7px] max-md:opacity-100",
        pinChrome && "pointer-events-auto"
      )}
    >
      <Cross2Icon
        className={cn(
          "h-4 w-4 max-md:h-5 max-md:w-5",
          mobileVariant !== "dialog" && "max-md:h-[30px] max-md:w-[30px] max-md:rounded-full max-md:bg-muted max-md:p-[7px]"
        )}
      />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  )

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={contentRef}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          MEDIUM_DIALOG_CLASS,
          !ownWidth.test(className ?? "") && MEDIUM_DIALOG_WIDTH,
          // Touch screens focus the dialog itself when it opens (below); it
          // needs no focus ring around the whole panel.
          "max-md:outline-none touch:outline-none",
          // duration-* alone transitions every property. Below xl the height
          // and position follow --app-height and --keyboard-inset, which
          // change when the keyboard opens; they must apply at once, or the
          // field being typed in is revealed against the old size and ends up
          // under the keyboard. The open and swipe animations still run.
          "max-xl:[transition-property:transform,opacity]",
          // Centre in the area above an iPad's keyboard, unless the caller
          // placed the dialog itself (top-[8vh]).
          !ownTop.test(className ?? "") &&
            (mobileVariant === "dialog"
              ? "max-xl:top-[calc(var(--app-height)/2)]"
              : "md:max-xl:top-[calc(var(--app-height)/2)]"),
          mobileDialogClasses[mobileVariant],
          mobileDialogPadding(mobileVariant, className),
          className
        )}
        onOpenAutoFocus={(event) => {
          if (onOpenAutoFocus) return onOpenAutoFocus(event)
          // Touch screens (phones in either orientation, tablets): focus the
          // dialog itself rather than its first field, so the keyboard doesn't
          // cover a dialog the person has only just opened. Fields with
          // autoFocus still get focus.
          if (isTouchViewport()) {
            event.preventDefault()
            ;(event.currentTarget as HTMLElement | null)?.focus({ preventScroll: true })
          }
        }}
        // Most dialogs open from a plain button or a menu, not a DialogTrigger,
        // so Radix has nothing to hand focus back to and it falls to the page.
        // On phones and touch screens (VoiceOver, Switch Control), and
        // everywhere for a dialog given returnFocusTo, return it to whatever
        // had focus when the dialog opened (or returnFocusTo when that has
        // gone), unless the caller handled it or something else (another
        // dialog) already took focus.
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event)
          const target = opener.current
          opener.current = null
          if (event.defaultPrevented || !(returnFocusTo || isMobile || isTouchViewport())) return
          const active = document.activeElement
          if (active && active !== document.body) return
          if (target?.isConnected) {
            target.focus({ preventScroll: true })
            if (document.activeElement === target) return
          }
          const standIn =
            typeof returnFocusTo === "string"
              ? Array.from(document.querySelectorAll<HTMLElement>(returnFocusTo)).find(isShown)
              : returnFocusTo?.()
          // The new layout may have remounted scrolled to the top, leaving the
          // stand-in off screen: let focus scroll it into view.
          if (standIn?.isConnected) standIn.focus()
        }}
        {...props}
      >
        <RecordOpener into={opener} />
        {mobileVariant === "sheet" && !pinChrome && <SheetGrabber />}
        {/* A dialog opened from this one finds its own way back. */}
        <ReturnFocusContext.Provider value={undefined}>{children}</ReturnFocusContext.Provider>
        {pinChrome ? (
          <div ref={setLayerNode} className="pointer-events-none absolute inset-x-0 top-0 z-20">
            <div className="sticky h-0">
              {mobileVariant === "sheet" && <SheetGrabber className="pointer-events-auto" />}
              {close}
            </div>
          </div>
        ) : (
          close
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 max-sm:gap-2 max-md:[&>button]:min-h-11",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogReturnFocus,
}
